import React from 'react';
import { ArrowDown, ArrowDownCircle } from 'lucide-react';
import { Button } from './button';
import Icon from '../molecules/Icon';
import { cn } from '../../lib/utils';

interface GoToBottomButtonProps {
  /**
   * Controls visibility of the button with animation
   */
  isVisible: boolean;
  /**
   * Opacity value (0-1) for smooth fade-in/out based on scroll position
   */
  opacity?: number;
  /**
   * Click handler for scrolling to bottom
   */
  onClick: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * GoToBottomButton - Floating action button for scrolling to bottom
 *
 * Features:
 * - Smooth fade-in/slide-up animation
 * - Centered positioning at bottom of container
 * - Hover and focus states with accessibility
 * - Icon-based visual indicator
 *
 * @example
 * ```tsx
 * <GoToBottomButton
 *   isVisible={showButton}
 *   onClick={scrollToBottom}
 * />
 * ```
 */
const GoToBottomButton: React.FC<GoToBottomButtonProps> = ({
  isVisible,
  opacity: opacityProp,
  onClick,
  className = '',
}) => {
  // Use provided opacity if available, otherwise fall back to isVisible-based opacity
  const opacity = opacityProp !== undefined ? opacityProp : isVisible ? 1 : 0;

  return (
    <Button
      onClick={onClick}
      variant="secondary-gray"
      size="icon-xl"
      className={cn(
        // Base styles
        'w-8 h-8 rounded-full',
        // Focus styles
        'focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
        // Transition for interactions and scroll-based opacity changes
        'transition-all duration-200 ease-in-out',
        isVisible ? 'translate-y-0 pointer-events-auto' : 'translate-y-4 pointer-events-none',
        className
      )}
      style={{
        opacity: opacity,
      }}
      aria-label="Go to bottom"
    >
      <Icon icon={ArrowDown} size={20} strokeWidth={1.5} color="currentColor" />
    </Button>
  );
};

export default GoToBottomButton;
